
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/layout/Layout';
import { Toaster } from './components/ui/Toaster';
import { ErrorBoundary } from './components/ErrorBoundary';
import { CommandPalette } from './components/CommandPalette';

const LandingPage = lazy(() => import('./pages/LandingPage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const BrowsePage = lazy(() => import('./pages/BrowsePage'));
const PromptDetailPage = lazy(() => import('./pages/PromptDetailPage'));
const PromptFormPage = lazy(() => import('./pages/PromptFormPage'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));
const ForgePage = lazy(() => import('./pages/ForgePage').then(m => ({ default: m.ForgePage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="flex h-screen w-full items-center justify-center">Loading...</div>;
  }
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  return <>{children}</>;
};

const AppRoutes: React.FC = () => (
  <Suspense fallback={<div className="flex h-screen w-full items-center justify-center bg-background">Loading Page...</div>}>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/browse" element={<Layout><BrowsePage /></Layout>} />
      <Route path="/forge" element={<Layout fullWidth><ForgePage /></Layout>} />
      <Route path="/studio" element={<Navigate to="/forge" replace />} />
      <Route path="/prompts/:id" element={<Layout><PromptDetailPage /></Layout>} />
      <Route path="/profile/:userId" element={<Layout><UserProfilePage /></Layout>} />
      
      <Route path="/dashboard" element={<ProtectedRoute><Layout><DashboardPage /></Layout></ProtectedRoute>} />
      <Route path="/prompts/new" element={<ProtectedRoute><Layout><PromptFormPage /></Layout></ProtectedRoute>} />
      <Route path="/prompts/:id/edit" element={<ProtectedRoute><Layout><PromptFormPage /></Layout></ProtectedRoute>} />
      
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  </Suspense>
);

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <CommandPalette />
            <Toaster />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
